import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { BazisConfigurationService } from '@bazis/configuration.service';

/**
 * **BazisLocaleService**
 *
 * Handles current UI locale selection.
 * Reads and stores selected language in localStorage and updates Transloco active language.
 */
@Injectable({ providedIn: 'root' })
export class BazisLocaleService {
    protected translocoService = inject(TranslocoService);

    protected configurationService = inject(BazisConfigurationService);

    /**
     * Internal locale override.
     * When set, it has priority over localStorage/config fallback.
     */
    private _locale: string;

    /**
     * Set locale override value.
     *
     * @param value - Locale/language code string (example: `en`, `ru`, `kk`).
     */
    set locale(value: string) {
        this._locale = value;
    }

    /**
     * Get current locale value.
     *
     * Resolution order:
     * - `_locale` override (when set)
     * - `localStorage.lang` if it exists and is present in `availableLanguages`
     * - first item from `availableLanguages`
     *
     * @returns Current locale/language code string.
     */
    get locale(): string {
        return (
            this._locale ||
            (this.configurationService.availableLanguages.indexOf(localStorage.getItem('lang')) > -1
                ? localStorage.getItem('lang')
                : this.configurationService.availableLanguages[0])
        );
    }

    /**
     * @param translocoService - Transloco service used to set active language.
     * @param configurationService - Configuration that provides list of available languages.
     */
    /**
     * Change application language and reload the page.
     *
     * Side effects:
     * - Saves language code to `localStorage.lang`
     * - Updates internal locale (`registerLocale()`)
     * - Sets Transloco active language
     * - Reloads current page (`location.reload()`)
     *
     * @param lang - Language code string.
     * @returns void
     */
    changeLang(lang: string) {
        localStorage.setItem('lang', lang);
        this.registerLocale(lang);
        this.translocoService.setActiveLang(lang);
        location.reload();
    }

    /**
     * Store locale override when culture code is provided.
     *
     * @param culture - Locale/language code string.
     * @returns void
     */
    private registerLocale(culture: string) {
        if (!culture) {
            return;
        }
        this.locale = culture;
    }
}
